import React from 'react';
import styled from '@emotion/styled';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Logo from './logo';
import Stack from './stack';
import { categories } from '../compositions/categories';

interface HeaderProps {
  children?: React.ReactNode;
  tagline?: boolean;
}

const HeaderFixedStyles = styled.div({
  position: 'fixed',
  top: 0,
  width: '100%',
});

const HeaderStyles = styled.header({
  display: 'flex',
  justifyContent: 'space-between',
  padding: '16px 14px 16px 24px',
  borderColor: 'light-dark(var(--surface-dark), var(--surface-light))',
  background: 'light-dark(var(--light), var(--dark));',
  borderBottom: '1px solid light-dark(var(--green-200), var(--green-800))',
  '@media (min-width: 640px)': {
    padding: '16px 16px 16px 36px',
  },
});

const HeaderInnerStyles = styled(Stack)({
  alignItems: 'center',
  gap: 16,
  '@media (min-width: 640px)': {
    gap: 24,
  },
});

const TaglineStyles = styled.span({
  fontFamily: '"Aleo", serif',
  fontWeight: 700,
  'font-optical-sizing': 'auto',
  fontStyle: 'italic',
  display: 'none',
  color: 'light-dark(var(--green-1000), var(--green-050))',
  '@media (min-width: 640px)': {
    display: 'flex',
    alignItems: 'center'
  },
});

const StyledLink = styled(Link)(() => ({
  textDecoration: 'none',
  display: 'flex'
}));

const CategorySelect = styled.select`
  appearance: base-select;
  position: relative;
  width: min(208px, calc(100vw - 32px));
  min-height: 50px;
  border: 1px solid light-dark(var(--green-300), var(--green-700));
  border-radius: 8px;
  background: light-dark(var(--action-tertiary-light), var(--action-tertiary-dark));
  color: light-dark(var(--action-tertiary-text-light), var(--action-tertiary-text-dark));
  font: inherit;
  font-weight: 700;
  padding: 0;
  transition: background 0.2s ease, border-color 0.2s ease;

  &:hover {
    background: light-dark(var(--action-tertiary-hover-light), var(--action-tertiary-hover-dark));
  }

  &:focus-visible {
    outline: 2px solid light-dark(var(--green-1000), var(--green-050));
    outline-offset: 2px;
  }

  &::picker-icon {
    content: "";
    position: absolute;
    inset-inline-end: 20px;
    top: 50%;
    translate: 0 -50%;
    inline-size: 16px;
    block-size: 16px;
    background: light-dark(var(--action-tertiary-text-light), var(--action-tertiary-text-dark));
    mask: center / contain no-repeat url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='none' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M5 7.5 10 12.5 15 7.5'/%3E%3C/svg%3E");
    transition: rotate 0.2s ease;
  }

  &:open::picker-icon {
    rotate: 180deg;
  }

  &::picker(select) {
    appearance: base-select;
    inset-inline-start: auto;
    inset-inline-end: anchor(right);
    border: 1px solid light-dark(var(--green-200), var(--green-800));
    border-radius: 12px;
    background: light-dark(var(--light), var(--dark));
    padding: 8px;
    margin-top: 8px;
    max-inline-size: calc(100vw - 32px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.12), 0 2px 10px rgba(0, 0, 0, 0.08);
  }

  &:not(:open)::picker(select) {
    display: none;
  }

  &:open::picker(select) {
    display: grid;
    grid-template-columns: 1fr;
    gap: 8px;
  }

  & option {
    display: block;
    border-radius: 8px;
    padding: 0;
    background: transparent;
    transition: background 0.15s ease;
  }

  & option:hover,
  & option:focus-visible,
  & option:checked {
    background: light-dark(var(--action-tertiary-hover-light), var(--action-tertiary-hover-dark));
  }

  & option::checkmark {
    display: none;
  }

  & selectedcontent {
    display: block;
    min-height: 50px;
    padding-inline-end: 34px;
  }

  & selectedcontent .option-content {
    grid-template-columns: auto 1fr;
  }

  & selectedcontent .option-icon {
    display: none;
  }
`;

const SelectedButton = styled.button({
  all: 'unset',
  display: 'contents',
});

const OptionContent = styled.div({
  display: 'grid',
  gridTemplateColumns: 'auto 1fr auto',
  alignItems: 'center',
  gap: 10,
  boxSizing: 'border-box',
  width: '100%',
  minHeight: 50,
  padding: '10px 12px',
  fontFamily: '"Aleo", serif',
  fontWeight: 700,
});

const OptionKanji = styled.span({
  color: 'light-dark(var(--green-700), var(--green-500))',
  fontFamily: '"M PLUS Rounded 1c", sans-serif',
  fontSize: 18,
  fontWeight: 500,
});

const OptionLabel = styled.span({
  color: 'light-dark(var(--green-1000), var(--green-050))',
  fontSize: 14,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const OptionIcon = styled.span({
  fontSize: 16,
});

const Header: React.FC<HeaderProps> = ({ children, tagline }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const selectedCategory = categories.find((category) => category.to === location.pathname);
  const selectedValue = selectedCategory?.to ?? '/';

  const onCategoryChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    navigate(event.target.value);
  };

  return (
      <HeaderFixedStyles>
        <HeaderStyles>
        <HeaderInnerStyles direction='horizontal'>
          <StyledLink to="/"><Logo /></StyledLink>
          {tagline && <TaglineStyles>Master Japanese Phrases</TaglineStyles>}
        </HeaderInnerStyles>
        <Stack direction='horizontal'>
          <CategorySelect aria-label="Categories" value={selectedValue} onChange={onCategoryChange}>
            <SelectedButton type="button">
              <selectedcontent />
            </SelectedButton>
            <option value="/">
              <OptionContent className="option-content">
                <OptionKanji>分類</OptionKanji>
                <OptionLabel>Categories</OptionLabel>
                <OptionIcon className="option-icon">🗂️</OptionIcon>
              </OptionContent>
            </option>
            {categories.map((category) => (
              <option key={category.to} value={category.to}>
                <OptionContent className="option-content">
                  <OptionKanji>{category.kanji}</OptionKanji>
                  <OptionLabel>{category.label}</OptionLabel>
                  <OptionIcon className="option-icon">{category.icon}</OptionIcon>
                </OptionContent>
              </option>
            ))}
          </CategorySelect>
        </Stack>
        </HeaderStyles>
        {children}
      </HeaderFixedStyles>
  );
};




export default Header;
